// src/components/CanvasArea.jsx
import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";

import useCanvas from "../hooks/useCanvas";
import LineList from "./LineList";
import ResponsiveOrderContainer from "./ResponsiveOrderContainer";
import { useData } from "../hooks/useData";
import { useDimensions } from "../hooks/useDimensions";
import { usePricing } from "../hooks/usePricing";
import useCanvasEvents from "../hooks/useCanvasEvents";

export default function CanvasArea() {
  const {
    canvas: savedCanvas,
    setCanvas,
    lines,
    addLine,
    selectedProduct,
  } = useData();
  const { updateDimensions } = useDimensions();
  const { updatePricing } = usePricing();
  const containerRef = useRef(null);

  // INITIAL CANVAS
  const fabricCanvas = useCanvas("canvas", { w: 100, h: 100 });

  // RESIZE CANVAS TO FIT CONTAINER
  useEffect(() => {
    if (!fabricCanvas || !containerRef.current) return;

    const resize = () => {
      const rect = containerRef.current.getBoundingClientRect();
      const width = rect.width * 0.98;
      const height = rect.height * 0.98;

      const canvasEl = fabricCanvas.lowerCanvasEl;
      canvasEl.width = width;
      canvasEl.height = height;
      canvasEl.style.width = width + "px";
      canvasEl.style.height = height + "px";

      fabricCanvas.setWidth(width);
      fabricCanvas.setHeight(height);
      fabricCanvas.setDimensions({ width, height });
      fabricCanvas.renderAll();
    };

    resize();
    window.addEventListener("resize", resize);
    const observer = new ResizeObserver(resize);
    observer.observe(containerRef.current);

    return () => {
      window.removeEventListener("resize", resize);
      observer.disconnect();
    };
  }, [fabricCanvas]);

  useEffect(() => {
    if (fabricCanvas && !savedCanvas) setCanvas(fabricCanvas);
  }, [fabricCanvas, savedCanvas, setCanvas]);

  useEffect(() => {
    if (lines.length === 0 && selectedProduct) addLine();
  }, [lines.length, selectedProduct, addLine]);

  useCanvasEvents({
    canvas: fabricCanvas,
    onUpdateDimensions: updateDimensions,
    onUpdatePricing: updatePricing,
  });

  // ZOOM LENS
  const [showZoom, setShowZoom] = useState(false);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!fabricCanvas) return;
    const check = () => {
      const overflow = fabricCanvas
        .getObjects()
        .some((o) => o.getScaledWidth() > fabricCanvas.getWidth() * 0.95);
      setShowZoom(overflow);
      setZoom(fabricCanvas.getZoom());
    };
    fabricCanvas.on("after:render", check);
    setTimeout(check, 500);
    return () => fabricCanvas.off("after:render", check);
  }, [fabricCanvas]);

  const zoomIn = () =>
    fabricCanvas.zoomToPoint(
      { x: fabricCanvas.width / 2, y: fabricCanvas.height / 2 },
      zoom * 1.25
    );
  const zoomOut = () =>
    fabricCanvas.zoomToPoint(
      { x: fabricCanvas.width / 2, y: fabricCanvas.height / 2 },
      zoom * 0.8
    );
  const zoomFit = () =>
    fabricCanvas.zoomToPoint(
      { x: fabricCanvas.width / 2, y: fabricCanvas.height / 2 },
      1
    );

  return (
    <>
      {/* CANVAS */}
      <Box
        ref={containerRef}
        id="canvas-container"
        sx={{
          width: "100%",
          height: { xs: 280, sm: 380, md: 460 },
          bgcolor: "#f8f8f8",
          border: "1px solid #ddd",
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          mb: 2,
        }}
      >
        <canvas id="canvas" />
        {showZoom && (
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              right: 16,
              zIndex: 10,
              bgcolor: "white",
              border: "2px solid #FF6B35",
              borderRadius: 3,
              p: 0.5,
            }}
          >
            <IconButton size="small" onClick={zoomOut}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ mx: 1, fontWeight: "bold" }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <IconButton size="small" onClick={zoomFit}>
              <FitScreenIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={zoomIn}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* LINE LIST */}
      <Box
        sx={{
          bgcolor: "background.paper",
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          mt: -1,
          position: "relative",
          zIndex: 2,
        }}
      >
        <LineList />
      </Box>

      {/* DRAWER */}
      <ResponsiveOrderContainer />
    </>
  );
}
